import { AuthenticationError } from "apollo-server-errors";
import UserToken from "@/lib/userToken";
import { AUTH_ERROR_MESSAGES } from "@/lib/errors";
import { metaSchemas, userRating } from "@/lib/constants";
import { isEnded } from "@/lib/metaUtil";
import dayjs from "@/lib/day";
import config from "@/lib/config";
import IdcToken from "@/lib/idcToken";

export async function createToken(req, res, user, signature) {
  const userToken = new UserToken(req, res);
  const payload = {
    id: user.token_id,
    hashedUserId: user?.user?.values?.user_id_hash,
    accessToken: user.access_token,
    refreshToken: user.refresh_token,
    tokenExpiredAt: dayjs().add(user.expires_in, "second").format(),
  };
  const token = await userToken.sign(payload, signature);
  req.userToken = payload;
  return token;
}

export async function createIdcToken(
  req,
  res,
  access_token,
  refresh_token,
  id_token,
  signature
) {
  const idcToken = new IdcToken(req, res);
  const payload = {
    id: id_token,
    accessToken: access_token,
    refreshToken: refresh_token,
  };
  const token = await idcToken.sign(payload, signature);
  req.idcToken = payload;
  return token;
}

export function sleep(n) {
  return new Promise((resolve) => setTimeout(resolve, n));
}

export function createPageInfo({ offset, limit, count }) {
  return {
    offset,
    limit,
    totalCount: count,
    hasNextPage: offset + limit < count,
    hasPreviousPage: offset > 0,
  };
}

export function thumbnailUrl(obj, _args, { req }) {
  if (!isAllowedRating(req, obj)) {
    return "/images/mask.png";
  }
  const noimage = "/images/noimage.png";
  return obj?.thumbnail?.org || noimage;
}

export function smallThumbnailUrl(obj, _args, { req }) {
  if (!isAllowedRating(req, obj)) {
    return "/images/mask.png";
  }
  const noimage = "/images/noimage.png";
  return obj?.thumbnail?.small || noimage;
}

export function mediumThumbnailUrl(obj, _args, { req }) {
  if (!isAllowedRating(req, obj)) {
    return "/images/mask.png";
  }
  const noimage = "/images/noimage.png";
  return obj?.thumbnail?.medium || noimage;
}

export function largeThumbnailUrl(obj, _args, { req }) {
  if (!isAllowedRating(req, obj)) {
    return "/images/mask.png";
  }
  const noimage = "/images/noimage.png";
  return obj?.thumbnail?.large || noimage;
}

export function isAuthenticated() {
  return (next) => async (root, args, context, info) => {
    if (!context.req.userToken) {
      throw new AuthenticationError(AUTH_ERROR_MESSAGES["notauthenticated"]);
    }
    return next(root, args, context, info);
  };
}
export function values(obj, name) {
  return obj?.values?.[name];
}

export function ownerOnly() {
  return (next) => async (root, args, context, info) => {
    const userToken = context.req?.userToken;
    if (!userToken) {
      throw new AuthenticationError(AUTH_ERROR_MESSAGES["notauthenticated"]);
    }
    if (userToken.id !== userToken.currentProfile) {
      throw new AuthenticationError(AUTH_ERROR_MESSAGES["notauthenticated"]);
    }
    return next(root, args, context, info);
  };
}

export function metaResolveType(meta, _context, _info) {
  if (meta.schema_key) {
    switch (meta.schema_key) {
      case metaSchemas.tvshow.key:
        return "TVShowMeta";
      case metaSchemas.season.key:
        return "SeasonMeta";
      case metaSchemas.episode.key:
        if (meta?.values?.is_live) {
          const ended = isEnded(meta?.values?.encode_end_date);
          // ライブが終わって見逃し対応ならEpisodeMetaとして扱う
          if (meta?.values?.catch_up_flag && ended) {
            return "EpisodeMeta";
          } else {
            return "LiveMeta";
          }
        } else {
          return "EpisodeMeta";
        }
      case metaSchemas.channel.key:
        return "ChannelMeta";
      case metaSchemas.linear.key:
        return "LinearMeta";
    }
  } else if (meta.schema_id) {
    switch (meta.schema_id) {
      case metaSchemas.tvshow.id:
        return "TVShowMeta";
      case metaSchemas.season.id:
        return "SeasonMeta";
      case metaSchemas.episode.id:
        if (meta?.values?.is_live) {
          const ended = isEnded(meta?.values?.encode_end_date);
          // ライブが終わって見逃し対応ならEpisodeMetaとして扱う
          if (meta?.values?.catch_up_flag && ended) {
            return "EpisodeMeta";
          } else {
            return "LiveMeta";
          }
        } else {
          return "EpisodeMeta";
        }
      case metaSchemas.channel.id:
        return "ChannelMeta";
      case metaSchemas.linear.id:
        return "LinearMeta";
    }
  }
  return null;
}

export function taxCalculation(price) {
  if (typeof price !== "number") return null;
  return Math.floor(price + price * config.taxRate);
}

export function genres(obj) {
  const genres = values(obj, "genres");
  // 複数ジャンルが入稿されたメタの場合
  // 親ジャンル1, 子ジャンル1, 親ジャンル2, 子ジャンル2
  // と入っているので先頭2件までにする
  if (genres.length > 2) {
    return genres.slice(0, 2);
  } else {
    return genres;
  }
}

export function isDateRangeIn(
  start,
  end,
  options = { buffer: null, default: true }
) {
  const now = (function () {
    if (options.buffer) {
      // {buffer: [1, "minute"]}
      return dayjs().add(...options.buffer);
    } else {
      return dayjs();
    }
  })();
  const startAt = dayjs(new Date(start));
  const endAt = dayjs(new Date(end));
  if (start && end) {
    return now.isBetween(startAt, endAt, null, "[)");
  } else if (start && !end) {
    return now.isSameOrAfter(startAt);
  } else if (!start && end) {
    return now.isBefore(end);
  } else {
    return options?.default;
  }
}

export function logicaErrorCode(e) {
  if (e?.extensions?.response) {
    console.dir(e.extensions.response);
    const body = e.extensions.response?.body;
    return body.error?.code;
  } else {
    return 500;
  }
}

export function logicaErrorBody(e) {
  if (e?.extensions?.response) {
    console.dir(e.extensions.response);
    const body = e.extensions.response?.body;
    return body;
  } else {
    return {};
  }
}

export function isAllowedRating(req, meta) {
  if (req.userToken && typeof req.userToken?.rating !== "undefined") {
    const rating = req.userToken.rating;
    const permitRating = userRating[rating].permitRating;
    return permitRating.includes(meta.values.rating);
  }
  return true;
}

export function ratingMask(req, meta, attr) {
  if (!isAllowedRating(req, meta)) {
    return meta[attr].replace(/./g, "*");
  }
  return meta?.[attr];
}

export function dateFormat(date, format = "YYYY/MM/DD") {
  if (!date) return null;
  return dayjs(date).format(format);
}

export function convertIdcGenderToSpooxSex(gender) {
  if (gender == "1") {
    return "man";
  } else if (gender == "2") {
    return "woman";
  } else {
    return "unknown";
  }
}
